import { Options, Splide, SplideSlide } from "@splidejs/react-splide";
import Image from "next/image";
import { getImageUrl } from "@/utils/utils";
import "@splidejs/react-splide/css";
import { useEffect, useRef } from "react";

import styles from "./Carousel.module.css";

export type LandingCarouselProps = {
    slides: {
        src: string;
        alt: string;
    }[];
    hideControls?: boolean;
    hideImages?: boolean;
};

export const LandingCarousel = ({
    slides,
    hideControls,
    hideImages,
}: LandingCarouselProps) => {
    const defaultConfig = {
        hideControls: false,
        hideImages: false,
    };

    const config = {
        ...defaultConfig,
        hideControls: Boolean(hideControls),
        hideImages: Boolean(hideImages),
    };

    const renderSlides = (cover = false) => {
        return slides.map((slide) => (
            <SplideSlide key={slide.src}>
                {cover ? (
                    <div
                        role="img"
                        aria-label={slide.alt}
                        style={{
                            width: "100%",
                            height: "100%",
                            overflow: "hidden",
                            backgroundImage: `url(${getImageUrl(slide.src)})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                        }}
                    ></div>
                ) : (
                    <Image
                        src={getImageUrl(slide.src)}
                        height={600}
                        width={600}
                        alt={slide.alt}
                    />
                )}
            </SplideSlide>
        ));
    };

    const mainOptions: Options = {
        type: "loop",
        perPage: 1,
        perMove: 1,
        gap: "0",
        pagination: false,
        height: "100%",
        arrows: false,
        role: "group",
    };

    const thumbsOptions: Options = {
        type: "slide",
        rewind: true,
        gap: "1rem",
        pagination: false,
        perPage: 3,
        fixedWidth: 120,
        fixedHeight: 70,
        cover: true,
        focus: "center",
        isNavigation: true,
        arrows: false,
        role: "listbox",
    };

    const mainRef = useRef<Splide>(null);
    const thumbsRef = useRef<Splide>(null);

    const announceSlideChange = (index: number) => {
        // fire global event
        const event = new CustomEvent("slideChange", {
            detail: {
                index,
            },
        });
        window.dispatchEvent(event);
    };

    useEffect(() => {
        // announce the first slide
        // announceSlideChange(0);

        // Define handler function for slide change events
        const handleSlideChangeEvent = (e: Event) => {
            console.log("Slide change event", (e as CustomEvent).detail.index);
            handleSlideChange((e as CustomEvent).detail.index);
        };

        // Add event listener
        window.addEventListener("slideChange", handleSlideChangeEvent);

        // Clean up event listener on unmount
        return () => {
            window.removeEventListener("slideChange", handleSlideChangeEvent);
        };
    }, []);

    const handleSlideChange = (index: number) => {
        if (!config.hideControls) {
            thumbsRef.current?.go(index);
        }
        if (!config.hideImages) {
            mainRef.current?.go(index);
        }
    };

    return (
        <>
            <style>
                {`
                .splide {
                height: 100%;}
                    .splide__track, .splide__track li {
                        height: 100%;
                        width: 100%;
                    }
                    
                    .splide__track--nav>.splide__list>.splide__slide.is-active {
                        border: none;
                    }

                    .splide__slide {
                        border-radius: 10px;
                        border: none;
                    }
            `}
            </style>
            <div
                style={{
                    height: config.hideImages ? undefined : "100%",
                    width: "100%",
                    overflow: "hidden",
                    position: config.hideImages ? "inherit" : "relative",
                    display: config.hideImages ? "inline-block" : "block",
                    minHeight: "110px",
                }}
            >
                {!config.hideImages && (
                    <>
                        <div
                            className={styles["goodlife-badge"]}
                        >
                            <svg
                                width="172"
                                height="23"
                                viewBox="0 0 172 23"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M0.00494877 13.8445C0.00494877 6.27273 5.80244 0 14.1659 0C20.3444 0 23.7962 3.77106 23.8284 8.55417H17.1426C16.858 7.09672 15.4006 5.86197 13.087 5.86197C9.41257 5.86197 6.43341 8.9674 6.43341 12.8325C6.43341 15.4628 8.08135 17.331 10.964 17.331C13.4656 17.331 15.4303 16.1903 16.0959 15.208H11.3426L12.2606 10.2962H23.7615C23.2221 17.9942 18.0284 23 9.97921 23C3.83283 23 0 19.1992 0 13.8445H0.00494877Z"
                                    fill="black"
                                />
                                <path
                                    d="M24.6829 13.7183C24.6829 6.1787 30.6709 0 38.97 0C45.0224 0 48.9492 3.80075 48.9492 9.31382C48.9492 16.7916 42.9612 23 34.6918 23C28.4514 23 24.6804 19.1349 24.6804 13.7183H24.6829ZM42.5183 10.3605C42.5183 7.79451 40.7763 5.86197 37.8936 5.86197C34.2192 5.86197 31.1138 8.8412 31.1138 12.6741C31.1138 15.2723 32.8558 17.1405 35.8028 17.1405C39.4154 17.1405 42.5183 14.1934 42.5183 10.3605Z"
                                    fill="black"
                                />
                                <path
                                    d="M49.4871 13.7183C49.4871 6.1787 55.4751 0 63.7742 0C69.8266 0 73.7534 3.80075 73.7534 9.31382C73.7534 16.7916 67.7654 23 59.496 23C53.2556 23 49.4846 19.1349 49.4846 13.7183H49.4871ZM67.3225 10.3605C67.3225 7.79451 65.5805 5.86197 62.6978 5.86197C59.0234 5.86197 55.918 8.8412 55.918 12.6741C55.918 15.2723 57.66 17.1405 60.607 17.1405C64.2196 17.1405 67.3225 14.1934 67.3225 10.3605Z"
                                    fill="black"
                                />
                                <path
                                    d="M76.5754 0.413269H85.0353C91.2139 0.413269 94.7621 4.02596 94.7621 9.19014C94.7621 16.8881 89.4719 22.5918 81.5514 22.5918H72.458L76.5754 0.413269ZM81.5811 17.3632C85.4461 17.3632 88.3287 14.1638 88.3287 9.88546C88.3287 6.58949 85.8271 5.63931 83.7363 5.63931H81.6776L79.5224 17.3608H81.5811V17.3632Z"
                                    fill="black"
                                />
                                <path
                                    d="M97.8953 0.413269H103.977L100.872 17.2049H109.171L108.157 22.5918H93.7729L97.8903 0.413269H97.8953Z"
                                    fill="black"
                                />
                                <path
                                    d="M114.019 0.413269H120.102L115.984 22.5918H109.902L114.019 0.413269Z"
                                    fill="black"
                                />
                                <path
                                    d="M122.002 0.412692H136.671L135.72 5.5447H127.134L126.375 9.53598H133.946L132.996 14.668H125.424L123.967 22.5887H117.885L122.002 0.410217V0.412692Z"
                                    fill="black"
                                />
                                <path
                                    d="M138.253 0.412692H152.922L151.971 5.5447H143.385L142.752 8.90252H150.007L149.057 14.0345H141.802L141.168 17.4245H150.071L149.121 22.5887H134.136L138.253 0.410217V0.412692Z"
                                    fill="black"
                                />
                                <path
                                    d="M165.388 8.78885L166.91 0.417786H161.279L159.757 8.78885H153.106L152.086 14.2302H158.737L157.174 22.574H162.803L164.367 14.2302H170.981L172 8.78885H165.388Z"
                                    fill="#9B7DEC"
                                />
                            </svg>
                        </div>
                        <Splide
                            style={{ height: "100%", width: "100%" }}
                            options={mainOptions}
                            ref={mainRef}
                            onActive={(e) => {
                                announceSlideChange(e.index);
                            }}
                        >
                            {renderSlides(true)}
                        </Splide>
                    </>
                )}
                {!config.hideControls && (
                    <Splide
                        style={{
                            position: config.hideImages
                                ? undefined
                                : "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 74,
                            padding: "1rem 0",
                        }}
                        onActive={(e) => {
                            announceSlideChange(e.index);
                        }}
                        options={thumbsOptions}
                        ref={thumbsRef}
                    >
                        {renderSlides()}
                    </Splide>
                )}
            </div>
        </>
    );
};
